"use client";

import { useState, useMemo, useEffect } from "react";
import { Loader2, Flame, RefreshCw, CheckSquare, Square, ImageOff } from "lucide-react";
import {
  type NFTInfo,
  FORGEPAD_FEE_PERCENT,
  TYPICAL_NFT_RENT,
  MAX_NFTS_PER_TX,
} from "@/lib/burn";

// Net amount per NFT after fee (used for display only)
const NET_RECLAIM_PER_NFT = TYPICAL_NFT_RENT * (1 - FORGEPAD_FEE_PERCENT / 100);

// Session limit to prevent excessive burning
const MAX_NFTS_PER_SESSION = 50;

// Known dead IPFS gateways - skip these URLs entirely to avoid console spam
const DEAD_GATEWAYS = [
  "cf-ipfs.com",
  "cloudflare-ipfs.com",
];

/**
 * Check if an image URL is from a known dead gateway
 * Returns true if the URL should be skipped
 */
function isDeadGateway(url: string | null | undefined): boolean {
  if (!url) return true;
  return DEAD_GATEWAYS.some((gateway) => url.includes(gateway));
}

/**
 * Get first 2 characters of NFT name for placeholder
 */
function getPlaceholderInitials(name: string): string {
  const cleaned = name.replace(/[^a-zA-Z0-9]/g, "");
  return cleaned.slice(0, 2).toUpperCase() || "??";
}

interface NFTsSectionProps {
  nfts: NFTInfo[];
  onBurnNFTs: (nfts: NFTInfo[]) => void;
  onRefresh: () => void;
  isRefreshing: boolean;
  isBurning: boolean;
}

export function NFTsSection({
  nfts,
  onBurnNFTs,
  onRefresh,
  isRefreshing,
  isBurning,
}: NFTsSectionProps) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [totalBurnedThisSession, setTotalBurnedThisSession] = useState(0);
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());

  // Track selected NFTs by mint string
  const [selectedMints, setSelectedMints] = useState<Set<string>>(new Set());

  // Limit displayed NFTs to session limit
  const displayableNFTs = useMemo(
    () => nfts.slice(0, MAX_NFTS_PER_SESSION),
    [nfts]
  );
  const hasMoreThan50 = nfts.length > MAX_NFTS_PER_SESSION;

  // Initialize all NFTs as selected when list changes
  useEffect(() => {
    const allMints = new Set(displayableNFTs.map((n) => n.mint.toBase58()));
    setSelectedMints(allMints);
    setImageErrors(new Set()); // Reset image errors on refresh
  }, [displayableNFTs]);

  // Hide section if no NFTs
  if (nfts.length === 0) {
    return null;
  }

  // Check if session limit reached
  const sessionLimitReached = totalBurnedThisSession >= MAX_NFTS_PER_SESSION;
  const remainingAllowed = MAX_NFTS_PER_SESSION - totalBurnedThisSession;

  // Get selected NFTs (respecting session limit)
  const selectedNFTs = displayableNFTs.filter((n) =>
    selectedMints.has(n.mint.toBase58())
  );
  const nftsToBurn = selectedNFTs.slice(
    0,
    Math.min(selectedNFTs.length, remainingAllowed)
  );

  // Calculate totals for selected NFTs
  const totalRent = nftsToBurn.length * TYPICAL_NFT_RENT;
  const platformFee = (totalRent * FORGEPAD_FEE_PERCENT) / 100;
  const netReclaim = totalRent - platformFee;

  const allSelected = selectedMints.size === displayableNFTs.length;
  const noneSelected = selectedMints.size === 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedMints(new Set());
    } else {
      setSelectedMints(new Set(displayableNFTs.map((n) => n.mint.toBase58())));
    }
  };

  const toggleNFT = (mint: string) => {
    const newSelected = new Set(selectedMints);
    if (newSelected.has(mint)) {
      newSelected.delete(mint);
    } else {
      newSelected.add(mint);
    }
    setSelectedMints(newSelected);
  };

  const handleImageError = (mint: string) => {
    // Silently mark as errored without logging to console
    setImageErrors((prev) => new Set(prev).add(mint));
  };

  const handleConfirm = () => {
    setShowConfirmModal(false);
    onBurnNFTs(nftsToBurn);
    // Increment session counter
    setTotalBurnedThisSession((prev) => prev + nftsToBurn.length);
    // Remove burned NFTs from selection
    const burnedMints = new Set(nftsToBurn.map((n) => n.mint.toBase58()));
    setSelectedMints((prev) => {
      const newSet = new Set(prev);
      burnedMints.forEach((m) => newSet.delete(m));
      return newSet;
    });
  };

  const numTransactions = Math.ceil(nftsToBurn.length / MAX_NFTS_PER_TX);

  /**
   * Render placeholder for NFT with no valid image
   */
  const renderPlaceholder = (name: string) => (
    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#1a1a1a] to-[#0a0a0a]">
      <div className="flex flex-col items-center justify-center text-[#555]">
        <div className="w-10 h-10 rounded-full bg-[#222] flex items-center justify-center text-[#f9cd4f] text-sm font-bold mb-1">
          {getPlaceholderInitials(name)}
        </div>
        <span className="text-xs text-[#444]">No image</span>
      </div>
    </div>
  );

  /**
   * Render NFT image or placeholder
   */
  const renderNFTImage = (nft: NFTInfo, mintStr: string, size: "normal" | "small" = "normal") => {
    const hasError = imageErrors.has(mintStr);
    const shouldSkip = isDeadGateway(nft.image) || hasError;

    if (shouldSkip) {
      return size === "small" ? (
        <div className="w-full h-full flex items-center justify-center bg-[#111] text-[#444]">
          <ImageOff className="w-6 h-6" />
        </div>
      ) : (
        renderPlaceholder(nft.name)
      );
    }

    return (
      <img
        src={nft.image}
        alt={nft.name}
        className="w-full h-full object-cover"
        onError={() => handleImageError(mintStr)}
        loading="lazy"
      />
    );
  };

  return (
    <>
      <div className="p-4 bg-[#0d0d0d] border border-[#222] rounded-xl">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Flame className="w-5 h-5 text-[#f9cd4f]" />
            <h3 className="font-semibold text-white">NFTs</h3>
            <span className="text-xs bg-[#f9cd4f]/20 text-[#f9cd4f] px-2 py-0.5 rounded-full">
              {nfts.length} found
            </span>
          </div>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="text-[#666] hover:text-[#888] transition-colors disabled:opacity-50"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </button>
        </div>

        <p className="text-sm text-[#888] mb-4">
          {sessionLimitReached
            ? "You've reached the session limit. Refresh to burn more."
            : "Permanently burn unwanted NFTs and reclaim rent."}
        </p>

        {/* Show note if more than 50 NFTs */}
        {hasMoreThan50 && !sessionLimitReached && (
          <p className="text-xs text-[#666] mb-3">
            Showing first 50 of {nfts.length}. Burn these first, then refresh to
            see more.
          </p>
        )}

        {/* Select All toggle */}
        {!sessionLimitReached && (
          <button
            onClick={toggleSelectAll}
            className="flex items-center gap-2 text-sm text-[#888] hover:text-white mb-3 transition-colors"
          >
            {allSelected ? (
              <CheckSquare className="w-4 h-4 text-[#f9cd4f]" />
            ) : (
              <Square className="w-4 h-4" />
            )}
            {allSelected ? "Deselect All" : "Select All"}
          </button>
        )}

        {/* NFT Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4 max-h-[400px] overflow-y-auto">
          {displayableNFTs.map((nft) => {
            const mintStr = nft.mint.toBase58();
            const isSelected = selectedMints.has(mintStr);

            return (
              <button
                key={mintStr}
                onClick={() => !sessionLimitReached && toggleNFT(mintStr)}
                disabled={sessionLimitReached}
                className={`relative flex flex-col bg-[#111] rounded-lg overflow-hidden transition-all ${
                  sessionLimitReached
                    ? "opacity-50 cursor-not-allowed"
                    : "hover:bg-[#1a1a1a] cursor-pointer"
                } ${isSelected ? "ring-2 ring-[#f9cd4f]" : ""}`}
              >
                {/* Checkbox overlay */}
                <div className="absolute top-2 left-2 z-10">
                  {isSelected ? (
                    <CheckSquare className="w-5 h-5 text-[#f9cd4f] drop-shadow-lg" />
                  ) : (
                    <Square className="w-5 h-5 text-white/60 drop-shadow-lg" />
                  )}
                </div>

                {/* NFT Image */}
                <div className="aspect-square w-full bg-[#0a0a0a] flex items-center justify-center overflow-hidden">
                  {renderNFTImage(nft, mintStr, "normal")}
                </div>

                {/* NFT Info */}
                <div className="p-2 text-left">
                  <div className="text-sm font-medium text-white truncate">
                    {nft.name}
                  </div>
                  {nft.collectionName && (
                    <div className="text-xs text-[#666] truncate">
                      {nft.collectionName}
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>

        {/* Burn button */}
        <button
          onClick={() => setShowConfirmModal(true)}
          disabled={
            isBurning || sessionLimitReached || noneSelected || nftsToBurn.length === 0
          }
          className="w-full py-3 bg-gradient-to-br from-[#f9cd4f] to-[#e6a800] text-black font-semibold rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
        >
          {isBurning ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Burning NFTs...
            </>
          ) : sessionLimitReached ? (
            <>
              <RefreshCw className="w-4 h-4" />
              Session limit reached — Refresh page
            </>
          ) : noneSelected ? (
            <>
              <Square className="w-4 h-4" />
              Select NFTs to burn
            </>
          ) : (
            <>
              <Flame className="w-4 h-4" />
              Burn {nftsToBurn.length} NFT{nftsToBurn.length > 1 ? "s" : ""} — Reclaim ~
              {netReclaim.toFixed(4)} SOL
            </>
          )}
        </button>

        {/* Multi-transaction note when > 5 selected */}
        {nftsToBurn.length > MAX_NFTS_PER_TX &&
          !sessionLimitReached &&
          !isBurning && (
            <p className="text-xs text-[#666] text-center mt-3">
              Selected {nftsToBurn.length} NFTs — will be processed in{" "}
              {numTransactions} transactions
            </p>
          )}
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div
          className="fixed inset-0 bg-black/85 flex items-center justify-center z-50 p-5"
          onClick={() => setShowConfirmModal(false)}
        >
          <div
            className="bg-[#0a0a0a] rounded-2xl border border-[#333] p-6 max-w-[450px] w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-xl font-bold text-center mb-4">
              <span className="mr-2">
                <Flame className="inline w-5 h-5" />
              </span>
              Burn NFTs Permanently
            </h2>

            {/* NFT preview grid */}
            <div className="grid grid-cols-4 gap-2 mb-4 max-h-[200px] overflow-y-auto">
              {nftsToBurn.slice(0, 12).map((nft) => {
                const mintStr = nft.mint.toBase58();
                return (
                  <div
                    key={mintStr}
                    className="aspect-square rounded-lg overflow-hidden bg-[#111]"
                  >
                    {renderNFTImage(nft, mintStr, "small")}
                  </div>
                );
              })}
              {nftsToBurn.length > 12 && (
                <div className="aspect-square rounded-lg bg-[#111] flex items-center justify-center text-[#666] text-sm font-medium">
                  +{nftsToBurn.length - 12}
                </div>
              )}
            </div>

            <div className="bg-[#111] rounded-lg p-4 mb-4">
              <div className="text-center text-sm text-[#aaa] mb-2">
                Burning {nftsToBurn.length} NFT{nftsToBurn.length > 1 ? "s" : ""}{" "}
                permanently
              </div>
              <div className="text-center text-[#f9cd4f] font-semibold text-lg">
                You receive: ~{netReclaim.toFixed(4)} SOL
              </div>
            </div>

            {nftsToBurn.length > MAX_NFTS_PER_TX && (
              <p className="text-xs text-[#888] text-center mb-4">
                Will be processed in {numTransactions} transactions. You'll need
                to approve each one.
              </p>
            )}

            <div className="bg-[#1a0a0a] border border-red-900/50 rounded-lg p-3 mb-4">
              <p className="text-xs text-red-400 text-center">
                This action is irreversible. These NFTs will be permanently
                destroyed.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmModal(false)}
                className="flex-1 py-3.5 bg-[#222] border-none rounded-lg text-[#888] text-base font-semibold cursor-pointer hover:bg-[#2a2a2a] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className="flex-1 py-3.5 bg-gradient-to-br from-[#f9cd4f] to-[#e6a800] border-none rounded-lg text-black text-base font-bold cursor-pointer hover:opacity-90 transition-opacity"
              >
                Burn & Reclaim
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
